import { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  useCustomAttributionControllerGetAllModelsQuery,
  useCustomAttributionControllerDeleteModelMutation,
  useCustomAttributionControllerGetInitiativeTypesQuery,
  useCustomAttributionControllerDeleteInitiativeTypeMutation,
  CustomAttributionModelWithWeights,
} from "~/ui-rtk/api/customAttributionApi"
import {
  AdChannel,
  InitiativeType,
  MetaObjective,
  TiktokGoal,
  OptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import {
  getInitiativeTypeLabel,
  getInitiativeTypeColorConfig,
} from "~/ui-rtk/constants/initiative-types"
import { DEFAULT_CUSTOM_ICON } from "~/ui-rtk/constants/initiative-type-colors"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"
import Button from "~/ui-rtk/components/ui/controls/Button"
import WeightEditModal from "./WeightEditModal"
import { CreateModelModal } from "./CreateModelModal"
import { CreateInitiativeTypeModal } from "./CreateInitiativeTypeModal"
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  HeartIcon,
  MegaphoneIcon,
  UserPlusIcon,
  ArrowPathIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from "@heroicons/react/24/outline"

import {
  AttributionSource,
  getMetricLabel,
  getSourceLabelForDisplay,
} from "../constants/attribution"

/**
 * Heroicon used in a column header/empty-state for an initiative type.
 * Predefined types keep their bespoke icons; custom types use the shared
 * default icon.
 */
function getInitiativeIcon(value: string, isCustom: boolean) {
  if (isCustom) return DEFAULT_CUSTOM_ICON
  switch (value as InitiativeType) {
    case InitiativeType.RETENTION:
      return HeartIcon
    case InitiativeType.DEMAND_GENERATION:
    case InitiativeType.DEMAND_GENERATION_2:
      return MegaphoneIcon
    case InitiativeType.DEMAND_CAPTURE_ACQUISITION:
      return UserPlusIcon
    case InitiativeType.DEMAND_CAPTURE_RETARGETING:
      return ArrowPathIcon
    case InitiativeType.DEMAND_CAPTURE_BROAD:
      return LinkIcon
    case InitiativeType.BRANDED_SEARCH:
    case InitiativeType.NON_BRAND_SEARCH:
      return MagnifyingGlassIcon
    case InitiativeType.PMAX:
      return CubeIcon
    default:
      return DEFAULT_CUSTOM_ICON
  }
}

interface GridRow {
  id: string
  modelId?: string
  exists: boolean
  adChannel: AdChannel
  initiativeType: string
  adChannelObjective?: MetaObjective | TiktokGoal
  adChannelOptimizationGoal?: OptimizationGoal
  weights?: Array<{
    id: string
    modelType: ModelType
    source: string
    metric: string
    weight: number
  }>
}

/** A board column: an initiative type (predefined or custom) and its models. */
interface BoardColumn {
  value: string
  isCustom: boolean
  /** CustomInitiativeType id for soft-delete; null for predefined/orphan. */
  id: string | null
  models: GridRow[]
}

interface WeightEdit {
  id?: string
  modelId: string
  modelType: ModelType
  source: AttributionSource
  metric: string
  weight: number
  isEdited: boolean
  errors?: WeightFormErrors
}

interface EditingWeightState {
  modelId: string
  type: ModelType
  currentValue?: number
}

interface WeightFormErrors {
  source?: string
  metric?: string
  weight?: string
}

interface RTKError {
  error?: string
  data?: {
    message?: string
  }
}

interface CustomAttributionBoardProps {
  adsetData?: Record<string, string | undefined>[]
  isAdsetDataLoading?: boolean
}

export function CustomAttributionBoard({
  adsetData,
}: CustomAttributionBoardProps = {}) {
  const {
    data: models = [] as CustomAttributionModelWithWeights[],
    isLoading: isLoadingModels,
    error: modelsError,
    refetch: refetchModels,
  } = useCustomAttributionControllerGetAllModelsQuery()

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const customTypes = useMemo(
    () => detailedTypes.filter(t => t.isCustom),
    [detailedTypes],
  )

  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false)
  const [editingWeight, setEditingWeight] = useState<EditingWeightState | null>(
    null,
  )
  const [weightEdits, setWeightEdits] = useState<WeightEdit[]>([])
  const [creatingInitiativeType, setCreatingInitiativeType] = useState<
    string | null
  >(null)
  const [isCreatingType, setIsCreatingType] = useState(false)
  const [editingModel, setEditingModel] =
    useState<CustomAttributionModelWithWeights | null>(null)
  const [editingModelType, setEditingModelType] = useState<ModelType | null>(
    null,
  )

  const [deleteInitiativeType] =
    useCustomAttributionControllerDeleteInitiativeTypeMutation()

  const handleEditModel = (modelId: string, modelType?: ModelType) => {
    const model = models.find(
      m => m.id === modelId,
    ) as CustomAttributionModelWithWeights
    if (!model) return

    setEditingModel(model)
    setEditingModelType(modelType || null)
  }

  const handleModalClose = () => {
    setIsWeightModalOpen(false)
    setEditingWeight(null)
    setWeightEdits([])
  }

  // Build board columns: predefined types (enum order) followed by the
  // company's active custom types, then group models into them. Models that
  // reference an unknown value (e.g. a soft-deleted custom) get an appended
  // column so they are never silently dropped.
  const columns = useMemo<BoardColumn[]>(() => {
    const ordered: Array<Omit<BoardColumn, "models">> = [
      ...Object.values(InitiativeType).map(value => ({
        value: value as string,
        isCustom: false,
        id: null,
      })),
      ...customTypes.map(c => ({
        value: c.value,
        isCustom: true,
        id: c.id,
      })),
    ]

    const groups: Record<string, GridRow[]> = {}
    ordered.forEach(col => {
      groups[col.value] = []
    })

    models.forEach(model => {
      const modelWithWeights = model as CustomAttributionModelWithWeights
      if (!modelWithWeights.initiativeType) return

      const row: GridRow = {
        id: modelWithWeights.id,
        modelId: modelWithWeights.id,
        exists: true,
        adChannel: modelWithWeights.adChannel,
        initiativeType: modelWithWeights.initiativeType,
        adChannelObjective: modelWithWeights.adChannelObjective,
        adChannelOptimizationGoal: modelWithWeights.adChannelOptimizationGoal,
        weights: modelWithWeights.weights?.map(w => ({
          ...w,
          modelType: w.modelType as ModelType,
        })),
      }

      if (!groups[modelWithWeights.initiativeType]) {
        groups[modelWithWeights.initiativeType] = []
        ordered.push({
          value: modelWithWeights.initiativeType,
          isCustom: true,
          id: null,
        })
      }
      groups[modelWithWeights.initiativeType].push(row)
    })

    return ordered.map(col => ({ ...col, models: groups[col.value] }))
  }, [models, customTypes])

  const [deleteModel] = useCustomAttributionControllerDeleteModelMutation()

  if (modelsError) {
    const error = modelsError as RTKError
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-500 mb-4">
          Error loading data:{" "}
          {error.data?.message || error.error || "Unknown error"}
        </div>
        <Button
          variant={{ variant: "solid", color: "blue" }}
          onClick={() => refetchModels()}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (isLoadingModels) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-basic-grey-inactive">Loading...</div>
      </div>
    )
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteModel({ id })
      void refetchModels()
    } catch (err) {
      console.error("Failed to delete model:", err)
    }
  }

  const handleDeleteInitiativeType = async (id: string | null) => {
    if (!id) return
    if (
      !window.confirm(
        "Delete this custom initiative type? It will be removed as a column and option. Existing assignments keep their stored value.",
      )
    ) {
      return
    }
    try {
      await deleteInitiativeType({ id }).unwrap()
    } catch (err) {
      console.error("Failed to delete initiative type:", err)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Initiative Types</h2>
        <Button
          variant={{ variant: "solid", color: "pink" }}
          size="sm"
          onClick={() => setIsCreatingType(true)}
          className="flex items-center gap-2 text-white"
        >
          <PlusIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Create Initiative Type</span>
        </Button>
      </div>
      <div className="flex gap-4 overflow-x-auto overflow-y-hidden">
        {columns.map((col, columnIndex) => {
          const { value: initiative, isCustom, id: customTypeId } = col
          const colModels = col.models
          const colors = getInitiativeTypeColorConfig(initiative, customTypes)
          const HeaderIcon = getInitiativeIcon(initiative, isCustom)
          return (
            <motion.div
              key={initiative}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.15,
                delay: columnIndex * 0.05,
                ease: "easeOut",
              }}
              className={`flex flex-col gap-4 min-w-[400px] bg-gradient-to-br ${colors.bg} border ${colors.border} p-system-lg rounded-xl shadow-lg`}
            >
              <div
                className={`flex justify-between items-center border-b ${colors.border} pb-system-sm`}
              >
                <div className="flex items-center gap-3">
                  <div className={`${colors.emptyIcon} p-2 rounded-lg`}>
                    <HeaderIcon className={`w-5 h-5 ${colors.accent}`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {getInitiativeTypeLabel(initiative, customTypes)}
                    </h2>
                  </div>
                </div>
                {isCustom && (
                  <Button
                    variant={{ variant: "text" }}
                    size="sm"
                    onClick={() => handleDeleteInitiativeType(customTypeId)}
                    className="!text-system-error-400 hover:!text-system-error-300 hover:!bg-system-error-500/20 p-2 rounded-lg transition-all duration-200"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <Button
                variant={{ variant: "text" }}
                size="sm"
                onClick={() => setCreatingInitiativeType(initiative)}
                className={`${colors.button} px-system-sm py-system-xs rounded-lg transition-all duration-200 flex items-center gap-2 hover:text-white`}
              >
                <PlusIcon className="w-4 h-4" />
                <span className="text-sm font-medium">Add Model</span>
              </Button>
              <div className="flex flex-col gap-3">
                <AnimatePresence mode="wait">
                  {colModels.length === 0 ? (
                    <motion.div
                      key="empty-state"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className={`flex flex-col items-center justify-center py-system-2xl px-system-md text-center bg-gradient-to-br ${colors.emptyBg} border-2 border-dashed rounded-xl`}
                    >
                      <div
                        className={`w-16 h-16 rounded-full ${colors.emptyIcon} flex items-center justify-center mb-system-md`}
                      >
                        <HeaderIcon className={`w-8 h-8 ${colors.accent}`} />
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">
                        No models yet
                      </h3>
                      <p className="text-system-gray-400 text-sm mb-system-md max-w-xs">
                        Create your first attribution model for{" "}
                        {getInitiativeTypeLabel(initiative, customTypes)}{" "}
                        initiatives
                      </p>
                      <Button
                        variant={{ variant: "text" }}
                        size="sm"
                        onClick={() => setCreatingInitiativeType(initiative)}
                        className={`${colors.button} px-system-md py-system-xs rounded-lg transition-all duration-200 hover:text-white`}
                      >
                        Create First Model
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="models-list"
                      className="flex flex-col gap-3"
                    >
                      <AnimatePresence>
                        {colModels.map(model => (
                          <motion.div
                            key={model.id}
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              duration: 0.25,
                              ease: "easeOut",
                              layout: { duration: 0.2 },
                            }}
                            className={`group bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-system-lg ${colors.hoverBg} ${colors.hoverBorder} transition-all duration-200 shadow-lg`}
                          >
                            <div className="flex flex-col gap-4">
                              {/* Header Section */}
                              <div className="flex justify-between items-start">
                                <div className="flex items-center gap-3">
                                  {/* Platform Icon */}
                                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                                    {model.adChannel === AdChannel.META && (
                                      <img
                                        src="/assets/img/sources/meta.svg"
                                        alt="Meta"
                                        className="w-6 h-6"
                                      />
                                    )}
                                    {model.adChannel === AdChannel.TIKTOK && (
                                      <img
                                        src="/assets/img/sources/tiktok.svg"
                                        alt="TikTok"
                                        className="w-6 h-6"
                                      />
                                    )}
                                    {model.adChannel ===
                                      AdChannel.PINTEREST && (
                                      <img
                                        src="/assets/img/sources/pinterest.svg"
                                        alt="Pinterest"
                                        className="w-6 h-6"
                                      />
                                    )}
                                    {model.adChannel ===
                                      AdChannel.GOOGLE_ADS && (
                                      <img
                                        src="/assets/img/sources/google_ads.png"
                                        alt="Google Ads"
                                        className="w-6 h-6 object-contain"
                                      />
                                    )}
                                    {model.adChannel === AdChannel.YOUTUBE && (
                                      <img
                                        src="/assets/img/sources/youtube.svg"
                                        alt="YouTube"
                                        className="w-6 h-6"
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="text-white font-semibold text-xl">
                                      {model.adChannel}
                                    </h3>
                                  </div>
                                </div>
                                <Button
                                  variant={{ variant: "text" }}
                                  size="sm"
                                  onClick={() => handleDelete(model.id)}
                                  className="opacity-0 group-hover:opacity-100 !text-system-error-400 hover:!text-system-error-300 hover:!bg-system-error-500/20 p-2 rounded-lg transition-all duration-200"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Configuration Section */}
                              <div className="space-y-2">
                                {model.adChannelObjective && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-system-gray-400 text-base font-medium">
                                      Objective:
                                    </span>
                                    <span className="px-system-xs py-system-3xs text-white text-base">
                                      {model.adChannelObjective
                                        .replace(/_/g, " ")
                                        .toLowerCase()}
                                    </span>
                                  </div>
                                )}

                                {model.adChannelOptimizationGoal && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-system-gray-400 text-base font-medium">
                                      Goal:
                                    </span>
                                    <span className="px-system-xs py-system-3xs text-white text-base">
                                      {model.adChannelOptimizationGoal
                                        .replace(/_/g, " ")
                                        .toLowerCase()}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Weights Section */}
                              <div
                                className={`space-y-3 pt-system-xs border-t ${colors.border}`}
                              >
                                {/* Direct Response Weights */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div>
                                        <div className="text-white font-medium text-base">
                                          Direct Response
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant={{ variant: "text" }}
                                      size="sm"
                                      onClick={() =>
                                        model.modelId &&
                                        handleEditModel(
                                          model.modelId,
                                          ModelType.DIRECT_RESPONSE,
                                        )
                                      }
                                      className="text-system-gray-400 hover:text-white hover:bg-white/10 px-system-xs py-system-3xs rounded text-sm transition-colors flex items-center gap-1"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                      Edit
                                    </Button>
                                  </div>

                                  {/* Direct Response Weight List */}
                                  <div className="space-y-1 ml-system-xs">
                                    {model.weights?.filter(
                                      w =>
                                        w.modelType ===
                                        ModelType.DIRECT_RESPONSE,
                                    ).length === 0 ? (
                                      <div className="text-sm text-system-gray-400 italic">
                                        No weights configured
                                      </div>
                                    ) : (
                                      model.weights
                                        ?.filter(
                                          w =>
                                            w.modelType ===
                                            ModelType.DIRECT_RESPONSE,
                                        )
                                        .map((weight, index) => (
                                          <div
                                            key={index}
                                            className="flex items-center justify-between p-system-xs bg-white/5 rounded border border-white/10"
                                          >
                                            <div className="flex-1">
                                              <div className="text-sm text-white font-medium">
                                                {getSourceLabelForDisplay(
                                                  weight.source,
                                                  weight.metric,
                                                )}
                                              </div>
                                              <div className="text-sm text-system-gray-400">
                                                {getMetricLabel(
                                                  weight.source,
                                                  weight.metric,
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-sm font-bold text-white bg-white/20 px-system-xs py-system-3xs rounded border border-white/20">
                                              {weight.weight.toFixed(2)}
                                            </div>
                                          </div>
                                        ))
                                    )}
                                  </div>
                                </div>

                                {/* Brand Value Weights */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div>
                                        <div className="text-white font-medium text-base">
                                          Brand Value
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant={{ variant: "text" }}
                                      size="sm"
                                      onClick={() =>
                                        model.modelId &&
                                        handleEditModel(
                                          model.modelId,
                                          ModelType.BRAND_VALUE,
                                        )
                                      }
                                      className="text-system-gray-400 hover:text-white hover:bg-white/10 px-system-xs py-system-3xs rounded text-sm transition-colors flex items-center gap-1"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                      Edit
                                    </Button>
                                  </div>

                                  {/* Brand Value Weight List */}
                                  <div className="space-y-1 ml-system-xs">
                                    {model.weights?.filter(
                                      w =>
                                        w.modelType === ModelType.BRAND_VALUE,
                                    ).length === 0 ? (
                                      <div className="text-sm text-system-gray-400 italic">
                                        No weights configured
                                      </div>
                                    ) : (
                                      model.weights
                                        ?.filter(
                                          w =>
                                            w.modelType ===
                                            ModelType.BRAND_VALUE,
                                        )
                                        .map((weight, index) => (
                                          <div
                                            key={index}
                                            className="flex items-center justify-between p-system-xs bg-white/5 rounded border border-white/10"
                                          >
                                            <div className="flex-1">
                                              <div className="text-sm text-white font-medium">
                                                {getSourceLabelForDisplay(
                                                  weight.source,
                                                  weight.metric,
                                                )}
                                              </div>
                                              <div className="text-sm text-system-gray-400">
                                                {getMetricLabel(
                                                  weight.source,
                                                  weight.metric,
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-sm font-bold text-white bg-white/20 px-system-xs py-system-3xs rounded border border-white/20">
                                              {weight.weight.toFixed(2)}
                                            </div>
                                          </div>
                                        ))
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )
        })}
      </div>

      {isCreatingType && (
        <CreateInitiativeTypeModal
          isOpen={true}
          onClose={() => setIsCreatingType(false)}
          onSuccess={refetchModels}
        />
      )}

      {isWeightModalOpen && (
        <WeightEditModal
          isOpen={isWeightModalOpen}
          onClose={handleModalClose}
          editingWeight={editingWeight}
          weightEdits={weightEdits}
          isLoadingModelWeights={false}
        />
      )}

      {creatingInitiativeType && (
        <CreateModelModal
          isOpen={true}
          onClose={() => setCreatingInitiativeType(null)}
          initiativeType={creatingInitiativeType}
          customTypes={customTypes}
          onSuccess={refetchModels}
          adsetData={adsetData}
        />
      )}

      {editingModel && (
        <CreateModelModal
          isOpen={true}
          onClose={() => {
            setEditingModel(null)
            setEditingModelType(null)
          }}
          initiativeType={editingModel.initiativeType}
          defaultActiveTab={editingModelType}
          customTypes={customTypes}
          onSuccess={refetchModels}
          adsetData={adsetData}
          existingModel={{
            id: editingModel.id,
            adChannel: editingModel.adChannel,
            adChannelObjective: editingModel.adChannelObjective,
            adChannelOptimizationGoal: editingModel.adChannelOptimizationGoal,
            weights: (editingModel.weights || []).map(w => ({
              id: w.id,
              modelType: w.modelType as ModelType,
              source: w.source,
              metric: w.metric,
              weight: w.weight,
            })),
          }}
        />
      )}
    </div>
  )
}
